import React, { useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoChevronDown } from "react-icons/io5";
import { mdiPower } from "@mdi/js";
import { DEVICE_TYPES, TRAITS, hasTrait, isType } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { UserService } from "../../../services/UserService";
import { User } from "../../../storage/store";
import { useToggleTop } from "../../../components/common/ToggleTop";
import AppSwitch from "../../../components/layout/app/AppSwitch";
import Sheet from "../../../components/layout/app/Sheet";
import MdiIcon from "../../../components/device/app/MdiIcon";
import {
  AlertDialog,
  DEFAULT_ERROR,
  MotorTabs,
  NumberField,
  SettingsPage,
  SettingsSkeleton,
  SliderField,
  UPDATED,
  Unavailable,
  UpdateButton,
  clamp,
  goBack,
  inRange,
  readConfigDevice,
  settingsPayload,
  toText,
  useAlive,
} from "./shared";
import "../../../assets/styles/app-settings.css";

const FORM_ID = "hx-set-motor-form";
const TABS_ID = "hx-set-motor-tab";
const HEADING_ID = "hx-set-motor-heading";
const fieldId = (motor, key) => `hx-set-m${motor + 1}-${key}`;

// Motor Hp picker. The controller takes hp × 100.
const HP_OPTIONS = [
  { label: "0.5", value: "50" },
  { label: "1.0", value: "100" },
  { label: "1.5", value: "150" },
  { label: "2.0", value: "200" },
  { label: "2.5", value: "250" },
];

const hpLabel = (value) => {
  const option = HP_OPTIONS.find((item) => item.value === toText(value));
  if (option) return option.label;
  const hp = Number(toText(value) || NaN) / 100;
  return Number.isFinite(hp) ? String(hp) : "–";
};

// The form's fields with the app's labels, placeholders and hints
// (MotorSettings.js). "hp" opens the Hp picker; "amps" is a 1-30 A slider the
// controller stores in tenths of an amp; the rest are number boxes.
const F = {
  motorType: {
    key: "motortype",
    label: "Motor Type",
    placeholder: "0-Submersible or 1-Compressor",
    hint: "Select appropriate Motor Type 0-Submersible & 1-Compressor",
  },
  hp: { key: "horsePower", label: "Motor Hp", kind: "hp", hint: "Select appropriate Motor Hp" },
  startCapacitor: {
    key: "startCapacitorOnTime",
    label: "Start Capacitor on Time",
    placeholder: "1-9 Sec",
    hint: "Range from 1 to 9 Seconds",
  },
  maxRunTime: { key: "maxRunTime", label: "Max Run Time", placeholder: "0-999 Min", hint: "Range from 0 to 999 Minutes" },
  highVoltage: {
    key: "highVoltageTrip",
    label: "High voltage trip",
    placeholder: "220-280 Volts",
    hint: "Range from 220 to 280 Volts",
  },
  lowVoltage: {
    key: "lowVoltageTrip",
    label: "Low voltage trip",
    placeholder: "170-210 Volts",
    hint: "Range from 170 to 210 Volts",
  },
  overLoadAmps: { key: "overLoadAmps", label: "Over load Amps", kind: "amps", hint: "Range from 1 to 30 Amps" },
  overLoadOffTime: {
    key: "overLoadOffTime",
    label: "Over Load off time",
    placeholder: "1-60 sec",
    hint: "Range from 1 to 60 Seconds",
  },
  dryRunAmps: { key: "dryRunAmps", label: "Dry Run Amps", kind: "amps", hint: "Range from 1 to 30 Amps" },
  dryRunOffTime: {
    key: "dryRunOffTime",
    label: "Dry Run off Time",
    placeholder: "0-999 sec",
    hint: "Range from 0 to 999 Seconds",
  },
  workingHours: {
    key: "workingHours",
    label: "Working Hours",
    placeholder: "0-2 mode",
    hint: "0-Day & Night, 1-Day Only and 2-Night Only",
  },
  onDelayTime: { key: "onDelayTime", label: "Ondelay Time", placeholder: "0-999 min", hint: "Range from 0 to 999 Minutes" },
  stlOffDelay: {
    key: "stlOffDelay",
    label: "Source Tank Low Level Off Delay",
    placeholder: "0-999 sec",
    hint: "Range from 0 to 999 Seconds",
  },
  stlOnDelay: {
    key: "stlOnDelay",
    label: "Source Tank Low Level On Delay",
    placeholder: "0-60 min",
    hint: "Range from 0 to 999 Seconds",
  },
  cyclicOnTime: {
    key: "cyclicOnTime",
    label: "Cyclic on Time",
    placeholder: "1-999 min",
    hint: "Range from 1 to 999 Minutes",
  },
  cyclicOffTime: {
    key: "cyclicOffTime",
    label: "Cyclic off Time",
    placeholder: "0-999 min",
    hint: "Range from 0 to 999 Minutes",
  },
};

// Each motor's form in the app's order. Motor 1 has the source tank delays;
// motor 2 has the motor type, start capacitor and cyclic times.
const MOTOR_FIELDS = [
  [
    F.hp,
    F.maxRunTime,
    F.highVoltage,
    F.lowVoltage,
    F.overLoadAmps,
    F.overLoadOffTime,
    F.dryRunAmps,
    F.dryRunOffTime,
    F.workingHours,
    F.onDelayTime,
    F.stlOffDelay,
    F.stlOnDelay,
  ],
  [
    F.motorType,
    F.hp,
    F.startCapacitor,
    F.maxRunTime,
    F.highVoltage,
    F.lowVoltage,
    F.overLoadAmps,
    F.overLoadOffTime,
    F.dryRunAmps,
    F.dryRunOffTime,
    { ...F.workingHours, placeholder: "0-2" },
    F.onDelayTime,
    F.cyclicOnTime,
    F.cyclicOffTime,
  ],
];

// The app's range messages in the app's order (checkMotorSettingValid). The
// app's own checks mostly never fire (&& and || mix-ups) and call a bad motor
// type "On delay Time"; these hold each field of every motor the controller
// has to the range its hint shows.
const CHECKS = [
  { key: "maxRunTime", min: 0, max: 999, message: "Motor Maximum Run Time Not in Range..!" },
  { key: "highVoltageTrip", min: 220, max: 280, message: "Motor High Voltage Trip Not in Range..!" },
  { key: "lowVoltageTrip", min: 170, max: 210, message: "Motor Low Voltage Trip Not in Range..!" },
  { key: "overLoadOffTime", min: 1, max: 60, message: "Motor Over Load Off Time Not in Range..!" },
  { key: "dryRunOffTime", min: 0, max: 999, message: "Motor Dry Run Off Time Not in Range..!" },
  { key: "workingHours", min: 0, max: 2, message: "Motor Working Hours Not in Range..!" },
  { key: "onDelayTime", min: 0, max: 999, message: "Motor On delay Time Not in Range..!" },
  { key: "stlOffDelay", min: 0, max: 999, message: "Motor Source Tank Low Level Off Delay Not in Range..!" },
  { key: "stlOnDelay", min: 0, max: 999, message: "Motor Source Tank Low Level On Delay Not in Range..!" },
  { key: "motortype", min: 0, max: 1, message: "Motor Type Not in Range..!" },
  { key: "startCapacitorOnTime", min: 1, max: 9, message: "Motor Start Capacitor On Time Not in Range..!" },
  { key: "cyclicOnTime", min: 1, max: 999, message: "Motor Cyclic On Time Not in Range..!" },
  { key: "cyclicOffTime", min: 0, max: 999, message: "Motor Cyclic Off Time Not in Range..!" },
];

const findProblem = (shown, settings) => {
  for (const check of CHECKS) {
    for (const motor of shown) {
      const inForm = MOTOR_FIELDS[motor].some((field) => field.key === check.key);
      if (inForm && !inRange(settings[motor][check.key], check.min, check.max)) return { ...check, motor };
    }
  }
  return null;
};

// TYPE_4 controllers run only motor 1 and TYPE_5 only motor 2; the other flow
// types run both.
const motorsFor = (device) => {
  if (device?.waterFlowType === "TYPE_4") return [0];
  if (device?.waterFlowType === "TYPE_5") return [1];
  return [0, 1];
};

// Both motors' settings, plus the flow type and sound that the 702 command
// repeats. Null unless the config has settings for both motors, which the app
// takes for granted.
const readMotors = (source) => {
  const config = source?.motorsConfig;
  if (!config?.[0]?.settings || !config?.[1]?.settings) return null;
  return {
    waterFlowType: config[0].waterFlowType,
    sound: config[0].sound,
    settings: [{ ...config[0].settings }, { ...config[1].settings }],
  };
};

// The 702 command's stg, field for field as the app's onUpdate builds it. Both
// motors are always sent.
const stgOf = ({ waterFlowType, sound, settings: [m1, m2] }) => ({
  waterFlowType: Number(waterFlowType),
  sound: Number(sound),
  m1Hp: Number(m1.horsePower),
  m1MaxRunTime: Number(m1.maxRunTime),
  m1HighVoltageTrip: Number(m1.highVoltageTrip),
  m1LowVoltageTrip: Number(m1.lowVoltageTrip),
  m1OverLoadAmps: Number(m1.overLoadAmps),
  m1OverLoadOffTime: Number(m1.overLoadOffTime),
  m1DryRunAmps: Number(m1.dryRunAmps),
  m1DryRunOffTime: Number(m1.dryRunOffTime),
  m1WorkingHours: Number(m1.workingHours),
  m1OndelayTime: Number(m1.onDelayTime),
  m1StlOffDelay: Number(m1.stlOffDelay),
  m1StlOnDelay: Number(m1.stlOnDelay),
  m2MotorType: Number(m2.motortype),
  m2Hp: Number(m2.horsePower),
  m2StartCapacitorOnTime: Number(m2.startCapacitorOnTime),
  m2MaxRunTime: Number(m2.maxRunTime),
  m2HighVoltageTrip: Number(m2.highVoltageTrip),
  m2LowVoltageTrip: Number(m2.lowVoltageTrip),
  m2OverLoadAmps: Number(m2.overLoadAmps),
  m2OverLoadOffTime: Number(m2.overLoadOffTime),
  m2DryRunAmps: Number(m2.dryRunAmps),
  m2DryRunOffTime: Number(m2.dryRunOffTime),
  m2WorkingHours: Number(m2.workingHours),
  m2OndelayTime: Number(m2.onDelayTime),
  m2CyclicOnTime: Number(m2.cyclicOnTime),
  m2CyclicOffTime: Number(m2.cyclicOffTime),
});

// Motor settings for water level controllers with trait 702
// (app/screens/DeviceSettings/MotorSettings.js): the Manual switch (705),
// each motor's settings, and Motor Fault Clear (704).
function MotorSettings() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const device = state?.data;
  const supported = isType(device, DEVICE_TYPES.WATER_LEVEL_CONTROLLER) && hasTrait(device, TRAITS.MOTOR_SETTINGS);
  const hasManual = hasTrait(device, TRAITS.MOTOR_MANUAL);
  const hasFaultClear = hasTrait(device, TRAITS.MOTOR_FAULT_CLEAR);
  const motors = motorsFor(device);
  // The detail screen passes the slide it was on as index; as in the app, any
  // slide after the first opens motor 2.
  const [motor, setMotor] = useState(() => {
    const wanted = state?.index ? 1 : 0;
    return motors.includes(wanted) ? wanted : motors[0];
  });
  // undefined while reading, null when the config has no motor settings.
  const [data, setData] = useState();
  // undefined while reading, null when the mode couldn't be read.
  const [manual, setManual] = useState();
  const [manualBusy, setManualBusy] = useState(false);
  const [hpOpen, setHpOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [invalid, setInvalid] = useState(null);

  useEffect(() => {
    if (!device || !supported) return undefined;
    let current = true;
    readConfigDevice(device.applianceId).then((fresh) => {
      if (current) setData(readMotors(fresh) ?? readMotors(device));
    });
    return () => {
      current = false;
    };
  }, [device, supported]);

  // The last state's mode is "1" in manual mode.
  useEffect(() => {
    if (!device || !supported || !hasManual) return undefined;
    let current = true;
    UserService.getLastStatebydeviceId(User.getPropertyId(), device.deviceId)
      .then((lastState) => {
        if (current) setManual(String(lastState?.mode) === "1");
      })
      .catch(() => {
        if (current) setManual(null);
      });
    return () => {
      current = false;
    };
  }, [device, supported, hasManual]);

  // Takes the user to the value that failed the check.
  useEffect(() => {
    if (invalid) document.getElementById(fieldId(invalid.motor, invalid.key))?.focus();
  }, [invalid]);

  if (!device) return <Redirect to="/dashboard" />;

  const change = (index, key, value) => {
    setData((current) => {
      const settings = current.settings.slice();
      settings[index] = { ...settings[index], [key]: value };
      return { ...current, settings };
    });
    if (invalid?.motor === index && invalid?.key === key) setInvalid(null);
  };

  // Sent the moment it's switched, as in the app; switched back if it fails.
  const toggleManual = async (next) => {
    if (manualBusy || typeof manual !== "boolean") return;
    setManual(next);
    setManualBusy(true);
    try {
      await sendControl(settingsPayload(device, { trait: Number(TRAITS.MOTOR_MANUAL), manual: Number(next) }));
    } catch (error) {
      if (alive.current) setManual(!next);
      toast(DEFAULT_ERROR, "red");
    } finally {
      if (alive.current) setManualBusy(false);
    }
  };

  const clearFault = async () => {
    setConfirmOpen(false);
    setBusy("Clearing motor fault…");
    try {
      await sendControl(settingsPayload(device, { trait: Number(TRAITS.MOTOR_FAULT_CLEAR), fltclr: 1 }));
    } catch (error) {
      toast(DEFAULT_ERROR, "red");
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  const update = async (event) => {
    event.preventDefault();
    if (busy || !data) return;
    const problem = findProblem(motors, data.settings);
    if (problem) {
      toast(problem.message, "red");
      setMotor(problem.motor);
      setInvalid({ motor: problem.motor, key: problem.key });
      return;
    }
    // The command repeats the controller's flow type and sound setting; if the
    // config doesn't have them, sending would write nulls to the pump.
    const stg = stgOf(data);
    if (!Number.isFinite(stg.waterFlowType) || !Number.isFinite(stg.sound)) {
      toast("This controller's setup is incomplete. Update it from the HASHh Connect app.", "red");
      return;
    }
    setBusy("Updating settings…");
    try {
      await sendControl(settingsPayload(device, { trait: Number(TRAITS.MOTOR_SETTINGS), stg }));
      toast(UPDATED, "green");
      if (!alive.current) return;
      setBusy(null);
      goBack(history);
    } catch (error) {
      if (alive.current) setBusy(null);
      toast(DEFAULT_ERROR, "red");
    }
  };

  const renderField = (field) => {
    const id = fieldId(motor, field.key);
    const value = data.settings[motor][field.key];

    if (field.kind === "hp") {
      return (
        <div key={field.key} className="hx-set-field">
          <span id={`${id}-label`} className="hx-set-label">
            {field.label}
          </span>
          <button
            type="button"
            id={id}
            className="hx-input hx-set-input hx-set-select"
            aria-haspopup="dialog"
            aria-labelledby={`${id}-label ${id}`}
            aria-describedby={`${id}-hint`}
            onClick={() => setHpOpen(true)}
          >
            <span>{hpLabel(value)}</span>
            <IoChevronDown aria-hidden="true" />
          </button>
          <p id={`${id}-hint`} className="hx-set-hint">
            {field.hint}
          </p>
        </div>
      );
    }

    if (field.kind === "amps") {
      const amps = Number(toText(value) || NaN) / 10;
      return (
        <SliderField
          key={field.key}
          id={id}
          label={field.label}
          min={1}
          max={30}
          step={0.1}
          value={clamp(amps, 1, 30)}
          display={Number.isFinite(amps) ? String(Math.round(amps * 10) / 10) : "–"}
          unit="Amps"
          hint={field.hint}
          onChange={(next) => change(motor, field.key, String(Math.round(next * 10)))}
        />
      );
    }

    return (
      <NumberField
        key={field.key}
        id={id}
        label={field.label}
        value={toText(value)}
        placeholder={field.placeholder}
        hint={field.hint}
        invalid={invalid?.motor === motor && invalid?.key === field.key}
        onChange={(next) => change(motor, field.key, next)}
      />
    );
  };

  const tabs = motors.length > 1;
  let form;
  if (data === undefined) {
    form = <SettingsSkeleton tall />;
  } else if (data === null) {
    form = <Unavailable>Motor settings aren't available for this controller.</Unavailable>;
  } else {
    form = (
      <>
        {tabs ? (
          <MotorTabs id={TABS_ID} panelId={FORM_ID} motors={motors} active={motor} onChange={setMotor} />
        ) : (
          <h2 id={HEADING_ID} className="hx-set-heading">
            Motor {motor + 1}
          </h2>
        )}
        <form
          id={FORM_ID}
          className="hx-set-card hx-set-form"
          noValidate
          onSubmit={update}
          role={tabs ? "tabpanel" : undefined}
          aria-labelledby={tabs ? `${TABS_ID}-${motor}` : HEADING_ID}
        >
          {MOTOR_FIELDS[motor].map(renderField)}
        </form>
      </>
    );
  }

  const currentHp = data ? toText(data.settings[motor].horsePower) : "";

  return (
    <SettingsPage
      title="Motor Settings"
      device={device}
      busy={busy}
      headerRight={<UpdateButton form={FORM_ID} busy={Boolean(busy)} disabled={!supported || !data} />}
    >
      {supported ? (
        <>
          {hasManual ? (
            <section className="hx-set-card">
              <div className="hx-set-toggle">
                <span className="hx-set-label">Manual</span>
                <AppSwitch
                  checked={manual === true}
                  onChange={toggleManual}
                  disabled={typeof manual !== "boolean" || manualBusy}
                  label="Manual"
                  trackColor="#FFA500"
                  knobColor="#fff"
                />
              </div>
              <p className="hx-set-hint">
                {manual === null ? "Couldn't read the current mode." : "Toggle to enable or disable manual Mode"}
              </p>
            </section>
          ) : null}

          {form}

          {hasFaultClear ? (
            <>
              <button
                type="button"
                className="hx-set-fault"
                aria-haspopup="dialog"
                aria-describedby="hx-set-fault-note"
                onClick={() => setConfirmOpen(true)}
              >
                Motor Fault Clear
                <MdiIcon path={mdiPower} size={40} color="red" />
              </button>
              <p id="hx-set-fault-note" className="hx-set-hint">
                Note: Click above to Clear Motor Fault
              </p>
            </>
          ) : null}
        </>
      ) : (
        <Unavailable />
      )}

      <Sheet open={hpOpen} onClose={() => setHpOpen(false)} title="Motor Hp">
        <div className="hx-set-chips">
          {HP_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="hx-set-chip"
              aria-pressed={option.value === currentHp}
              onClick={() => {
                change(motor, "horsePower", option.value);
                setHpOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Sheet>

      <AlertDialog
        open={confirmOpen}
        type="confirmation"
        title="Clear Motor Fault"
        message="Are you sure you want to clear Motor Fault ?"
        onClose={() => setConfirmOpen(false)}
        onConfirm={clearFault}
      />
    </SettingsPage>
  );
}

export default MotorSettings;
